import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';
import { useAuth } from '../../../../../../context/AuthContext';
import { useStore } from '../../../../../../context/StoreContext';
import { SMIIFY_API } from '../../../../../../config/api';
import DiscountMembershipList from './DiscountMembershipList/DiscountMembershipList';
import DiscountMembershipForm from './DiscountMembershipForm/DiscountMembershipForm';
import styles from './DiscountMembershipManager.module.css';

const API_BASE = `${SMIIFY_API}/discount-memberships`;

const getStoreId = (store) => store?.id || store?._id || '';
const getPartnerId = (user) => user?.partnerId || user?.partner || user?._id || '';

const Notice = ({ notice, onClose }) => (notice ? (
    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px', padding: '9px 12px', borderRadius: 9, fontFamily: 'Nunito, sans-serif', fontSize: 12.5, fontWeight: 700, background: notice.type === 'ok' ? '#f1f9f3' : '#fff4f3', color: notice.type === 'ok' ? '#005432' : '#870013' }}>
        {notice.type === 'ok' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
        <span style={{ flex: 1 }}>{notice.text}</span>
        <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', display: 'grid' }}><X size={14} /></button>
    </div>
) : null);

/**
 * Discount membership plans. Access comes from MembershipManager:
 *   canCreate (create_discount), canEdit (edit_discount), canDeactivate (deactivate_discount)
 */
export default function DiscountMembershipManager({ onBack, canCreate = false, canEdit = false, canDeactivate = false }) {
    const [view, setView] = useState('LIST');
    const [memberships, setMemberships] = useState([]);
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [notice, setNotice] = useState(null);

    const { user } = useAuth();
    const { activeStore } = useStore();
    const storeId = getStoreId(activeStore);
    const partnerId = getPartnerId(user);
    const isBranch = Boolean(storeId) && storeId !== 'global_hq';

    const flash = (type, text) => { setNotice({ type, text }); if (type === 'ok') setTimeout(() => setNotice(null), 3500); };
    const authHeaders = (json = false) => ({
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${localStorage.getItem('smiify_token')}`,
        'x-store-id': storeId,
    });

    const fetchMemberships = useCallback(async () => {
        if (!partnerId || !isBranch) { setMemberships([]); setIsLoadingList(false); return; }
        setIsLoadingList(true);
        try {
            const res = await fetch(`${API_BASE}?storeId=${encodeURIComponent(storeId)}&partnerId=${encodeURIComponent(partnerId)}`, { headers: authHeaders() });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not load discount plans.');
            setMemberships(Array.isArray(json.data) ? json.data : []);
        } catch (error) {
            setMemberships([]);
            flash('error', error.message || 'Could not load discount plans.');
        } finally {
            setIsLoadingList(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerId, storeId, isBranch]);

    useEffect(() => { fetchMemberships(); }, [fetchMemberships]);

    const openNew = () => { if (canCreate) { setEditData(null); setView('FORM'); } };
    const openEdit = (m) => { if (canEdit) { setEditData(m); setView('FORM'); } };

    const handleSavePlan = async (formData) => {
        if (!isBranch) { flash('error', 'Select a branch first.'); return { success: false }; }
        if (editData ? !canEdit : !canCreate) { flash('error', 'You do not have access to do this.'); return { success: false }; }
        setIsSaving(true);
        try {
            const res = await fetch(`${API_BASE}/save`, {
                method: 'POST',
                headers: authHeaders(true),
                body: JSON.stringify({ ...formData, planId: editData?.id || null, partnerId, storeId }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not save the plan.');
            await fetchMemberships();
            setView('LIST');
            setEditData(null);
            flash('ok', editData ? 'Plan updated.' : 'Plan published.');
            return { success: true };
        } catch (error) {
            flash('error', error.message || 'Could not save the plan.');
            return { success: false };
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleStatus = async (planId) => {
        if (!canDeactivate) return;
        try {
            const res = await fetch(`${API_BASE}/toggle-status`, {
                method: 'PATCH',
                headers: authHeaders(true),
                body: JSON.stringify({ planId, partnerId, storeId }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not change the status.');
            await fetchMemberships();
            flash('ok', json.message || 'Status changed.');
        } catch (error) {
            flash('error', error.message || 'Could not change the status.');
        }
    };

    return (
        <div className={styles.managerWrapper}>
            <Notice notice={notice} onClose={() => setNotice(null)} />
            {!isBranch && <Notice notice={{ type: 'error', text: 'Select a branch from the top menu to manage its discount plans.' }} onClose={() => {}} />}

            {view === 'LIST' && (
                <DiscountMembershipList
                    memberships={memberships}
                    onAddNew={openNew}
                    onEdit={openEdit}
                    onToggleStatus={handleToggleStatus}
                    onBack={onBack}
                    isLoading={isLoadingList}
                    canCreate={canCreate && isBranch}
                    canEdit={canEdit}
                    canDeactivate={canDeactivate}
                />
            )}

            {view === 'FORM' && (
                <DiscountMembershipForm
                    initialData={editData}
                    existingTiers={memberships}
                    onCancel={() => { setView('LIST'); setEditData(null); }}
                    onSave={handleSavePlan}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
}